import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { PresetCollection, SocialCollection, SocialMedia } from '@app/v2/event/types/event-social-media.type'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { map, Observable, Subject } from 'rxjs'
import { EventTimingException, TimingExceptionPayload } from '../types/event-timing-exception.type'
import { ActiveTimelineEventWrapperParams, ArchivedTimelineEventWrapperParams, EventWrapperModel, EventWrapperModelBuildParams } from '../types/event-wrapper-model.type'
import {
  Checkin,
  Event,
  EventFiltersCountResponse,
  EventRegistrationsCount,
  EventsListQuery,
  EventsListResponse,
  EventTimingsResponse,
  UpdateCheckinEventPayload,
  UpdateDayTimingsPayload,
  UpdateEventTimingsPayload
} from '../types/event.type'

export type TimelineRefreshRequest = { type: 'list' } | { type: 'event'; eventId: number }

export const EVENT_PERSONAS_PER_PAGE = 5

@Injectable()
export class EventService {
  public retrieving$: Subject<boolean> = new Subject<boolean>()
  public timelineRefreshRequested$ = new Subject<TimelineRefreshRequest>()
  public pendingTimelineRefresh: TimelineRefreshRequest | null = null

  constructor(private _http: HttpClient, private _authService: AuthService) {}

  public requestTimelineRefresh(request: TimelineRefreshRequest): void {
    this.pendingTimelineRefresh = request
    this.timelineRefreshRequested$.next(request)
  }

  /**
   * Create an event
   * @param workspace_id {number} - The id of the workspace to create the event in
   * @returns {Observable<Response<Event>>} - An observable of the event
   */
  public createEvent(workspace_id: number): Observable<Response<Event>> {
    return this._http.post<Response<Event>>(`${environment.planningApi}organizations/${workspace_id}/events`, { workspace_id })
  }

  /**
   * Get an event by its id
   * @param event_id {number} - The id of the event to get
   * @returns {Observable<Response<Event>>} - An observable of the event
   */
  public getEvent(workspace_id: number, event_id: number): Observable<Response<Event>> {
    return this._http.get<Response<Event>>(`${environment.planningApi}organizations/${workspace_id}/events/${event_id}`)
  }

  /**
   * Same payload shape as one row from GET .../events (timeline list), not full show/wizard event.
   */
  public getEventListItem(workspace_id: number, event_id: number): Observable<Response<Event>> {
    return this._http.get<Response<Event>>(`${environment.planningApi}organizations/${workspace_id}/events/${event_id}/list-item`)
  }

  /**
   * Update event data - only sends changed fields
   * @param event_id {number} - The id of the event to update
   * @param eventData {Partial<Event>} - Partial event data containing only changed fields
   * @returns {Observable<Response<Event>>} - An observable of the updated event
   */
  public updateEventData(workspace_id: number, event_id: number, eventData: Partial<Event>): Observable<Response<Event>> {
    return this._http.patch<Response<Event>>(`${environment.planningApi}organizations/${workspace_id}/events/${event_id}`, eventData, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * @description Permanently delete an event.
   * @param workspace_id {number} - The id of the workspace (organization).
   * @param event_id {number} - The id of the event.
   * @param message {string} - The message to send with the request.
   * @returns {Observable<Response<[]>>} - An observable of the response from the API.
   */
  public deleteEventPermanently(workspace_id: number, event_id: number, message: string): Observable<Response<[]>> {
    return this._http.delete<Response<[]>>(`${environment.planningApi}organizations/${workspace_id}/events/${event_id}`, {
      body: { message },
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Updates a checkin's location/details (patch on blur or marker change).
   * Checkin id is in the URL; body is only location_details.
   * @param workspace_id - Workspace id
   * @param event_id - Event id
   * @param checkin_id - Checkin id (in URL)
   * @param location_details - Partial location_details to patch
   * @returns {Observable<Response<Checkin>>} - An observable of the updated checkin
   */
  public updateCheckinEvent(
    workspace_id: number,
    event_id: number,
    checkin_id: number,
    location_details: Partial<UpdateCheckinEventPayload['location_details']>
  ): Observable<Response<Checkin>> {
    return this._http.patch<Response<Checkin>>(
      `${environment.planningApi}organizations/${workspace_id}/events/${event_id}/checkins/${checkin_id}`,
      { location_details },
      {
        context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
      }
    )
  }

  /*
   * Create a social link for an event
   * @param workspace_id {number} - The id of the workspace to create the social link in
   * @param event_id {number} - The id of the event to create the social link for
   * @param link {string} - The link to create the social link for
   * @returns {Observable<SocialMedia>} - An observable of the created social link
   */
  public createEventSocialLink(workspace_id: number, event_id: number, link: string): Observable<Response<SocialMedia>> {
    return this._http.post<Response<SocialMedia>>(`${environment.planningApi}organizations/${workspace_id}/events/${event_id}/social-link`, { url: link })
  }

  /**
   * Get the presets collections for the event
   * @returns {Observable<Response<PresetCollection[]>>} - An observable of the presets collections
   */
  public getPresetsCollections(organization_id: number): Observable<Response<PresetCollection>> {
    return this._http.get<Response<PresetCollection>>(`${environment.planningApi}organizations/${organization_id}/social-media`)
  }

  /**
   * Create a presets collection for the organization
   * @param organization_id {number} - The id of the organization to create the presets collection for
   * @param name {string} - The name of the presets collection
   * @param social_media {SocialMedia[]} - The social media to create the presets collection for
   * @returns {Observable<Response<PresetCollection>>} - An observable of the created presets collection
   */
  public createPresetsCollections(organization_id: number, name: string, social_media: SocialMedia[]): Observable<Response<PresetCollection>> {
    return this._http.patch<Response<PresetCollection>>(`${environment.planningApi}organizations/${organization_id}/social-media`, { name, social_media })
  }

  public updatePresetsCollections(organization_id: number, collections: SocialCollection[]): Observable<Response<PresetCollection>> {
    return this._http.put<Response<PresetCollection>>(`${environment.planningApi}organizations/${organization_id}/social-media`, { social_collection: collections })
  }

  public deletePresetsCollections(organization_id: number, collections: SocialCollection[]): Observable<Response<PresetCollection>> {
    return this._http.put<Response<PresetCollection>>(`${environment.planningApi}organizations/${organization_id}/social-media`, { social_collection: collections })
  }

  /**
   * Get events filter counts for timeline header chips.
   * @param organizationId {number} - Workspace/organization id.
   * @returns {Observable<EventFiltersCountResponse>} - Event counts response.
   */
  public getEventsFiltersCount(organizationId: number): Observable<EventFiltersCountResponse> {
    return this._http.get<EventFiltersCountResponse>(`${environment.planningApi}organizations/${organizationId}/events/count`)
  }

  /**
   * Get events list using v2 timeline API contract.
   * Omitting `page`/`perPage` returns all matching events unpaginated.
   * Flagged requests (e.g. the invitation tracker event picker) bypass the global error toast; the caller shows its own alert.
   * @param query {EventsListQuery} - Query params for timeline listing.
   * @returns {Observable<EventsListResponse>} - Events response.
   */
  public getEventsList(query: EventsListQuery): Observable<EventsListResponse> {
    const params = new URLSearchParams()
    if (query.page != null) params.set('page', query.page.toString())
    if (query.perPage != null) params.set('perPage', query.perPage.toString())

    const search = query.search?.trim()
    if (search) params.set('search', search)
    if (query.status) params.set('status', query.status)
    if (query.isArchived) params.set('is_archived', '1')
    if (query.flag) params.set('flag', query.flag)

    const context = query.flag ? new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true) : undefined
    return this._http.get<EventsListResponse>(`${environment.planningApi}organizations/${query.organizationId}/events?${params.toString()}`, { context })
  }

  /**
   * Gets registration counts for the provided events list.
   * The API response is keyed by event slug.
   * @param events {Event[]} - The events to get the registration counts for
   * @returns {Observable<Event[]>} - An observable of the events with the registration counts
   */
  public getEventCounts(events: Event[]): Observable<Event[]> {
    return this._http
      .post<EventRegistrationsCount>(
        `${environment.registrationsApi}count/${this._authService.currentWorkspace.slug}`,
        {
          events: events.map((event) => event.slug).filter((slug): slug is string => !!slug)
        },
        {
          context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
        }
      )
      .pipe(
        map((registrationsCount) => {
          if (!registrationsCount) return events

          Object.keys(registrationsCount).forEach((slug) => {
            const event = events.find((currentEvent) => currentEvent.slug === slug)
            if (!event) return

            const total = registrationsCount[slug]?.total || 0
            const attended = registrationsCount[slug]?.attended || 0

            ;(event as Event & { registration_count?: number; registration_count_display?: string }).registration_count = total
            ;(event as Event & { registration_count_display?: string }).registration_count_display = total ? `${attended}/${total}` : ''
          })

          return events
        })
      )
  }

  /**
   *
   * @param eventId
   * @returns Observable
   */
  public cloneEvent(workspace_id: number, eventId: number): Observable<Response<Event>> {
    return this._http.post<Response<Event>>(`${environment.planningApi}organizations/${workspace_id}/events/${eventId}/clone`, {})
  }

  /**
   * Build the active timeline wrapper model
   * @param params {ActiveTimelineEventWrapperParams} - The parameters for the active timeline wrapper model
   * @returns {EventWrapperModel} - The active timeline wrapper model
   */
  public buildActiveTimelineWrapperModel(params: ActiveTimelineEventWrapperParams): EventWrapperModel {
    return this._buildEventWrapperModel({
      ...params,
      archived: false
    })
  }

  /**
   * Build the archived timeline wrapper model
   * @param params {ArchivedTimelineEventWrapperParams} - The parameters for the archived timeline wrapper model
   * @returns {EventWrapperModel} - The archived timeline wrapper model
   */
  public buildArchivedTimelineWrapperModel(params: ArchivedTimelineEventWrapperParams): EventWrapperModel {
    return this._buildEventWrapperModel({
      ...params,
      canEdit: false,
      canCreate: true,
      headerCloning: false,
      headerCloningMessage: '',
      timelineBannerLayout: true,
      archived: true,
      personas: params.personas ?? [],
      personasTotal: params.personasTotal ?? null,
      personasLoading: params.personasLoading ?? false,
      createPersonaPending: params.createPersonaPending ?? false,
      createPersonaApiError: params.createPersonaApiError ?? null,
      loadingCounts: params.loadingCounts,
      canModifyEventOrPersonas: false
    })
  }

  /**
   * Build the event wrapper model
   * @param params {EventWrapperModelBuildParams} - The parameters for the event wrapper model
   * @returns {EventWrapperModel} - The event wrapper model
   */
  private _buildEventWrapperModel(params: EventWrapperModelBuildParams): EventWrapperModel {
    return {
      event: params.event,
      isLast: params.isLast,
      header: {
        canEdit: params.canEdit,
        canCreate: params.canCreate,
        cloning: params.headerCloning,
        cloningMessage: params.headerCloningMessage
      },
      card: {
        events: params.events,
        timelineBannerLayout: params.timelineBannerLayout,
        archived: params.archived,
        loadingCounts: params.loadingCounts,
        bannerSaveInProgress: params.bannerSaveInProgress,
        personas: params.personas,
        personasTotal: params.personasTotal,
        personasLoading: params.personasLoading,
        createPersonaPending: params.createPersonaPending,
        createPersonaApiError: params.createPersonaApiError,
        canModifyEventOrPersonas: params.canModifyEventOrPersonas
      }
    }
  }

  /**
   * Get event timings
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @returns Observable of the timings response
   */
  public getEventTimings(organizationId: number, eventId: number): Observable<Response<EventTimingsResponse>> {
    return this._http.get<Response<EventTimingsResponse>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timings`)
  }

  /**
   * Update a single timing row by id
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @param timingId - The timing row id (numeric)
   * @param payload - Full row body (includes id)
   * @returns Observable of the update response
   */
  public updateDayTimings(organizationId: number, eventId: number, timingId: string | number, payload: UpdateDayTimingsPayload): Observable<Response<unknown>> {
    return this._http.patch<Response<unknown>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timings/${timingId}`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Replace all event timings (public range + per-day rows)
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @param payload - Bulk timings payload
   * @returns Observable of the update response
   */
  public updateTimings(organizationId: number, eventId: number, payload: UpdateEventTimingsPayload): Observable<Response<EventTimingsResponse>> {
    return this._http.patch<Response<EventTimingsResponse>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timings/`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Get the timing exceptions for an event
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @returns Observable of the timing exceptions
   */
  public getTimingExceptions(organizationId: number, eventId: number): Observable<Response<EventTimingException[]>> {
    return this._http.get<Response<EventTimingException[]>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timing-exceptions`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Create a timing exception for an event
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @param payload - The timing exception payload
   * @returns Observable of the created timing exception
   */
  public createTimingException(organizationId: number, eventId: number, payload: TimingExceptionPayload): Observable<Response<EventTimingException>> {
    return this._http.post<Response<EventTimingException>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timing-exceptions`, payload, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Update a timing exception for an event
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @param exceptionId - The timing exception ID
   * @param payload - The timing exception payload
   * @returns Observable of the updated timing exception
   */
  public updateTimingException(organizationId: number, eventId: number, exceptionId: string | number, payload: TimingExceptionPayload): Observable<Response<EventTimingException>> {
    return this._http.patch<Response<EventTimingException>>(
      `${environment.planningApi}organizations/${organizationId}/events/${eventId}/timing-exceptions/${exceptionId}`,
      payload,
      { context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true) }
    )
  }

  /**
   * Delete a timing exception for an event
   * @param organizationId - The organization ID
   * @param eventId - The event ID
   * @param exceptionId - The timing exception ID
   * @returns Observable of the deleted timing exception
   */
  public deleteTimingException(organizationId: number, eventId: number, exceptionId: string | number): Observable<Response<string>> {
    return this._http.delete<Response<string>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timing-exceptions/${exceptionId}`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * @description Reset all timing exceptions for an event (bulk delete).
   * @param organizationId - The organization ID.
   * @param eventId - The event ID.
   * @returns Observable of the delete response.
   */
  public resetTimingExceptions(organizationId: number, eventId: number): Observable<Response<string>> {
    return this._http.delete<Response<string>>(`${environment.planningApi}organizations/${organizationId}/events/${eventId}/timing-exceptions`, {
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }
}
